// ShareBite aggregate dashboard/analytics counts (Task 7.5).
//
// Pure aggregation functions that compute the numeric counts surfaced on the
// Restaurant Overview, NGO Overview, Impact panels, Admin analytics view, and
// the Achievements Earned figure. Every value these functions produce is a
// whole number greater than or equal to zero, derived from a snapshot of the
// underlying activity data. The functions are platform-independent and
// side-effect free; callers (AnalyticsService callables, dashboard views) pass
// in the records they wish to aggregate.
//
// Impact-relevant meal totals follow the platform-wide rule that only
// Donations whose status is exactly "Completed" contribute meals (Req 24.4,
// 24.5, 31.1); every other status contributes nothing.
//
// Design reference: design.md "Property 23: Aggregate counts are non-negative
// integers" and "AnalyticsService".
// Requirements:
//   20.1 - Restaurant Overview shows four non-negative integer counts
//          (Meals Donated, Meals Completed, Active Donations, Pending Donations).
//   21.1 - NGO Overview shows Meals Received, Active Claims, and Completed
//          Claims, each a non-negative integer.
//   23.9 - Admin analytics shows counts of Restaurants, NGOs, meals donated,
//          meals received, Completed Donations, and Expired Donations, each a
//          non-negative integer.
//   31.1 - The Achievements Earned count is a non-negative integer.
/**
 * Whole-number floor at zero. Defends the count contract against negative or
 * fractional meal-count inputs so every produced figure remains a
 * non-negative integer (Req 20.1, 21.1, 23.9, 31.1).
 */
function nonNegativeInt(value) {
    if (!Number.isFinite(value) || value <= 0)
        return 0;
    return Math.floor(value);
}
/** Sum Meal Counts over donations whose status is exactly Completed. */
function completedMeals(donations) {
    let total = 0;
    for (const d of donations) {
        if (d.status === "Completed")
            total += nonNegativeInt(d.mealCount);
    }
    return total;
}
/**
 * Compute the Restaurant Overview counts for a Restaurant's donations.
 *
 * Pure: reads only its argument and returns a fresh object. Every field is a
 * non-negative integer (Req 20.1).
 */
export function restaurantOverviewCounts(donations) {
    let mealsCompleted = 0;
    let activeDonations = 0;
    let pendingDonations = 0;
    for (const d of donations) {
        if (d.status === "Completed")
            mealsCompleted += 1;
        else if (d.status === "Available" || d.status === "Reserved")
            activeDonations += 1;
        else if (d.status === "Draft")
            pendingDonations += 1;
    }
    return {
        mealsDonated: completedMeals(donations),
        mealsCompleted,
        activeDonations,
        pendingDonations,
    };
}
/**
 * Compute the NGO Overview counts for an NGO's claimed donations.
 *
 * Pure: reads only its argument and returns a fresh object. Every field is a
 * non-negative integer (Req 21.1).
 */
export function ngoOverviewCounts(claims) {
    let activeClaims = 0;
    let completedClaims = 0;
    for (const c of claims) {
        if (c.status === "Completed")
            completedClaims += 1;
        else if (c.status === "Reserved" || c.status === "Collected")
            activeClaims += 1;
    }
    return {
        mealsReceived: completedMeals(claims),
        activeClaims,
        completedClaims,
    };
}
/**
 * Compute a Restaurant's Impact totals.
 *
 * `mealsDonated` sums Completed-donation meals; `ngosServed` counts the
 * distinct reserving NGOs across Completed donations. Pure; both fields are
 * non-negative integers (Req 20.7).
 */
export function restaurantImpactTotals(donations) {
    const served = new Set();
    for (const d of donations) {
        if (d.status === "Completed" && d.reservedBy !== null) {
            served.add(d.reservedBy);
        }
    }
    return {
        mealsDonated: completedMeals(donations),
        ngosServed: served.size,
    };
}
/**
 * Compute the Admin analytics counts (Req 23.9).
 *
 * Pure: reads only its arguments and returns a fresh object. Every field is a
 * non-negative integer.
 *
 * @param restaurantCount Number of Restaurant accounts in the system.
 * @param ngoCount        Number of NGO accounts in the system.
 * @param donations       All Donations in the system.
 */
export function adminAnalyticsCounts(restaurantCount, ngoCount, donations) {
    let completedDonations = 0;
    let expiredDonations = 0;
    for (const d of donations) {
        if (d.status === "Completed")
            completedDonations += 1;
        else if (d.status === "Expired")
            expiredDonations += 1;
    }
    const meals = completedMeals(donations);
    return {
        restaurants: nonNegativeInt(restaurantCount),
        ngos: nonNegativeInt(ngoCount),
        // Meals donated and meals received both reflect Completed-donation meals:
        // the meals a Restaurant donated are exactly the meals an NGO received.
        mealsDonated: meals,
        mealsReceived: meals,
        completedDonations,
        expiredDonations,
    };
}
/**
 * Compute the Achievements Earned count (Req 31.1).
 *
 * Returns the number of earned achievements as a non-negative integer. Pure.
 *
 * @param achievements The User's earned achievements (any array-like).
 */
export function achievementsEarnedCount(achievements) {
    return nonNegativeInt(achievements.length);
}
//# sourceMappingURL=aggregateCounts.js.map